import AdaptiveDialog, {
  AdaptiveDialogAction,
  AdaptiveDialogCancel,
  AdaptiveDialogClose,
  AdaptiveDialogContent,
  AdaptiveDialogFooter,
  AdaptiveDialogHeader,
  AdaptiveDialogTitle,
  AdaptiveDialogTrigger,
} from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import { AdaptiveMenuItem } from "@/lib/components/ui/adaptive-menu/adaptive-menu";
import Field, { FieldGroup, FieldLabel } from "@/lib/components/ui/field/field";
import Pen from "@/lib/components/ui/icons/pen";
import Input from "@/lib/components/ui/input/input";
import Spinner from "@/lib/components/ui/spinner";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRenameThread } from "../../hooks/use-rename-thread";
import type { Thread } from "../../threads.utils";

export function ThreadsListRename({
  thread,
  assistantId,
}: {
  thread: Thread;
  assistantId: string;
}) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(thread.title || "");
  const previousTitle = thread.title || "";

  const inputRef = useRef<HTMLInputElement>(null);

  const { rename, isPending } = useRenameThread({
    thread,
    onError: () => setTitle(previousTitle),
    onSuccess: () => setOpen(false),
    assistantId,
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || trimmed === previousTitle) {
      setOpen(false);
      return;
    }
    rename(title, previousTitle);
  }

  function handleCancel() {
    setTitle(previousTitle);
    setOpen(false);
  }

  function handleOpenChange(newOpen: boolean) {
    setOpen(newOpen);
    if (newOpen) {
      // Reset to current thread title when opening
      setTitle(previousTitle);
    }
  }

  return (
    <AdaptiveDialog open={open} onOpenChange={handleOpenChange}>
      <AdaptiveDialogTrigger asChild>
        <AdaptiveMenuItem onSelect={(event) => event.preventDefault()}>
          <Pen /> {t("threads_action_rename")}
        </AdaptiveMenuItem>
      </AdaptiveDialogTrigger>
      <AdaptiveDialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <AdaptiveDialogClose type="button" disabled={false} />
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <AdaptiveDialogHeader>
            <AdaptiveDialogTitle>
              {t("threads_rename_title")}
            </AdaptiveDialogTitle>
          </AdaptiveDialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="thread-title">
                {t("threads_rename_field_label")}
              </FieldLabel>
              <Input
                id="thread-title"
                ref={inputRef}
                value={title}
                required
                onChange={(event) => setTitle(event.target.value)}
                placeholder={t("threads_rename_field_placeholder")}
              />
            </Field>
          </FieldGroup>
          <AdaptiveDialogFooter>
            <AdaptiveDialogClose asChild>
              <AdaptiveDialogCancel type="button" onClick={handleCancel}>
                {t("common_cancel")}
              </AdaptiveDialogCancel>
            </AdaptiveDialogClose>
            <AdaptiveDialogAction type="submit" disabled={isPending}>
              {isPending && <Spinner />}
              {t("common_save")}
            </AdaptiveDialogAction>
          </AdaptiveDialogFooter>
        </form>
      </AdaptiveDialogContent>
    </AdaptiveDialog>
  );
}
